import Cookies from "js-cookie";

import { parseQueryString } from "@brexhq/next/src/utils/parseQueryString";

import { getRefCodeFromUrl } from "../components/modules/LeadForm/helpers/helpers";
import { getEditionMappings } from "../utils/getEditionMapping";

export interface AttributionMetadata {
  fbp: string | undefined;
  fbc: string | undefined;
  gclid: string | undefined;
  msclkid: string | undefined;
  liFatId: string | undefined;
}

/**
 * Have a function to get refcode based on the attributes.
 * We want to detail better what kind of ref codes are being sent
 * as opposed to the generic "attributionToken" and "selfAtteseted"
 */
export const getRefCodesForSignup = (
  location: Window["location"],
  partnerRefToken: string | undefined,
) => {
  /**
   * Fixed lengths for the respective type of ref code
   */
  const PARTNER_REF_TOKEN_LENGTH = 14;
  const USER_REF_TOKEN_LENGTH = 7;
  /**
   * If undefined, we can default to empty strings.
   * Another check to get the values directly from urlParams (if exists)
   * since historically there were instances with users blocking cookies
   */
  const ref_code = getRefCode() || parseQueryString("ref_code", location) || "";
  const rc = getUserRefCode() || parseQueryString("rc", location) || "";

  /**
   * set them all originally as null to conditionally
   * add values that apply to them using the two cookies above.
   */
  let ads: string | null = null;
  let generalPartner: string | null = null;
  let onboardingApiPartner: string | null = null;
  let firstParty: string | null = null;

  /**
   * ads refcode logic
   */
  if (ref_code.startsWith("pmk_")) {
    ads = ref_code;
  }

  /**
   * Right now no distinct way to determine general
   * partner ref codes. Going to assign every time.
   */
  generalPartner = ref_code;

  /**
   * Logic for onboarding api's partner referral token.
   * must be 14 digits long alphanumeric string.
   *
   * Alternatively, if a partner referral token already exists
   * make that value take precedence
   */
  if (ref_code.length === PARTNER_REF_TOKEN_LENGTH || partnerRefToken) {
    onboardingApiPartner = partnerRefToken || ref_code;
  }

  /**
   * First party ref code logic
   */
  const isUserRef = rc.length === USER_REF_TOKEN_LENGTH;
  const isEmailRef = rc.startsWith("c");

  if (isUserRef || isEmailRef) {
    firstParty = rc;
  }

  return {
    ads,
    generalPartner,
    onboardingApiPartner,
    firstParty,
  };
};

export const getApplicableRefCodes = (pathname: string) => {
  const signupUrlRegex = /signup\/\w/;
  if (signupUrlRegex.test(pathname)) {
    return getRefCodeFromUrl(pathname);
  }
  return getRefCode() ?? getUserRefCode() ?? null;
};

export const getRefCode = () => Cookies.get("ref_code");

export const getUserRefCode = () => Cookies.get("rc");

export const getGCLID = () => {
  return Cookies.get("gclid");
};

export const getFBC = () => {
  return Cookies.get("_fbc");
};

export const getFBP = () => {
  return Cookies.get("_fbp");
};

export const getEdition = () => {
  const editionValue = Cookies.get("edition");
  return getEditionMappings(editionValue);
};

export const getLiFatId = () => {
  return Cookies.get("li_fat_id");
};

export const getMSCLKID = () => {
  return Cookies.get("msclkid");
};

export const getAttributionMetadata = (
  fbp: string | undefined,
  fbc: string | undefined,
  gclid: string | undefined,
  msclkid: string | undefined,
  liFatId: string | undefined,
): AttributionMetadata | undefined => {
  if (!fbp && !fbc && !gclid && !msclkid && !liFatId) {
    return;
  }
  return { fbp, fbc, gclid, msclkid, liFatId };
};
